import { onScopeDispose } from 'vue'

/**
 * Every object url capture creates is revoked by capture.
 *
 * Capture is a single page that does not reload between the document list, a form and the
 * accreditation views, so a url nobody revokes is held for the life of the session — whether it
 * came from a document's fetched content or from a file the user has just picked. Callers track
 * what they create here instead of each keeping its own bookkeeping, and everything outstanding
 * is released when the scope that created it goes away.
 */
export default function objectUrls(): {
  releaseObjectUrl: (url: string) => void
  releaseObjectUrls: () => void
  trackObjectUrl: (url: string) => string
} {
  const tracked = new Set<string>()
  let disposed = false

  /** Releases one url — a second click, or a re-picked file, replacing what it pointed at. */
  function releaseObjectUrl(url: string): void {
    if (!tracked.has(url)) {
      return
    }

    tracked.delete(url)
    URL.revokeObjectURL(url)
  }

  function releaseObjectUrls(): void {
    tracked.forEach((url) => URL.revokeObjectURL(url))
    tracked.clear()
  }

  /**
   * Returns the url, so a caller can track and bind it in one step. A url made after its scope
   * has gone — a fetch that finished after the user navigated away — has nobody left to show it
   * or to release it, so it is revoked on arrival.
   */
  function trackObjectUrl(url: string): string {
    if (disposed) {
      URL.revokeObjectURL(url)

      return url
    }

    tracked.add(url)

    return url
  }

  // Silent when there is no scope to dispose of: a service that fetches content is a legitimate
  // caller, and it hands the url to whoever asked rather than holding it itself.
  onScopeDispose(() => {
    disposed = true
    releaseObjectUrls()
  }, true)

  return {
    releaseObjectUrl,
    releaseObjectUrls,
    trackObjectUrl,
  }
}
